import * as Sentry from '@sentry/react';
import { useRouteError } from 'react-router-dom';

function isChunkLoadError(error: unknown): boolean {
  if (error instanceof Error) {
    return (
      error.message.includes('Failed to fetch dynamically imported module') ||
      error.message.includes('Importing a module script failed') ||
      error.message.includes('error loading dynamically imported module')
    );
  }
  return false;
}

export function ChunkErrorBoundary() {
  const error = useRouteError();

  if (isChunkLoadError(error)) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 p-8 text-center">
        <h1 className="text-xl font-semibold text-gray-900">
          New version available
        </h1>
        <p className="max-w-md text-gray-600">
          We've released an update. Please reload the page to get the latest
          version.
        </p>
        <button
          type="button"
          className="rounded-lg bg-blue-600 px-6 py-2.5 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          onClick={() => window.location.reload()}
        >
          Reload page
        </button>
      </div>
    );
  }

  // Report non-chunk errors to Sentry and show a recovery UI instead of
  // re-throwing, which would blow up the entire page via React Router's
  // default error boundary.
  if (error instanceof Error) {
    Sentry.captureException(error);
  }

  return (
    <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 p-8 text-center">
      <h1 className="text-xl font-semibold text-gray-900">
        Something went wrong
      </h1>
      <p className="max-w-md text-gray-600">
        We ran into an unexpected error. Please try again later or contact support if the issue persists.
      </p>
      <button
        type="button"
        className="rounded-lg bg-blue-600 px-6 py-2.5 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        onClick={() => window.history.back()}
      >
        Go back
      </button>
    </div>
  );
}
